import { QuestionElementProps } from "@/components/questionnaire/components/element.type.ts";
import {
  ApiCustomFieldValue,
  ApiField,
  ApiFieldTypes,
  ApiTableTypes,
} from "@/types/custom-field.types.ts";
import {
  ApiFormElementWriteToFieldSettingsEdit,
  ApiFormElementWriteToFieldTargetType,
} from "@/types/forms/form-edit.types.ts";
import { ApiFormView } from "@/types/forms/form-view.types.ts";

export const getWriteToFieldSlug = (
  elementId: number,
  fieldId: number,
  rowIndex: number = 0,
  fieldType?: ApiField["type"]
) => {
  const fieldSlugPart = fieldType ? `${fieldType}_${fieldId}` : fieldId;

  return rowIndex === 0
    ? `write_to_field_${elementId}_${fieldSlugPart}`
    : `write_to_field_${elementId}_${rowIndex}_${fieldSlugPart}`;
};

export const getWriteToFieldRowsKey = (elementId: number) => `write_to_field_${elementId}_rows`;

export const getWriteToFieldSavedValue = (
  questionnaireForm: ApiFormView | undefined,
  elementId: number,
  rowIndex: number,
  field: ApiField
): ApiCustomFieldValue | undefined =>
  questionnaireForm?.answers
    .find((answer) => answer.form_element_id === elementId)
    ?.answer_write_to_field?.[rowIndex]?.cells?.find(
      (cell) => cell.field_id === field.id && cell.field_type === field.type
    )?.value;

const isEditableWriteToField = (field: ApiField) =>
  field.type !== ApiFieldTypes.changed_by && field.type !== ApiFieldTypes.effective_to;

export const getWriteToFieldEffectiveFromSlug = (elementId: number, rowIndex: number = 0) =>
  rowIndex === 0
    ? `write_to_field_${elementId}_effective_from`
    : `write_to_field_${elementId}_${rowIndex}_effective_from`;

export const getWriteToFieldTable = (
  element: QuestionElementProps["element"],
  settings: ApiFormElementWriteToFieldSettingsEdit
) => {
  if (
    settings.target_type !== ApiFormElementWriteToFieldTargetType.table ||
    settings.table_id == null
  ) {
    return undefined;
  }

  return element.tables?.find((table) => table.id === settings.table_id);
};

export const isSystemWriteToFieldTable = (
  element: QuestionElementProps["element"],
  settings: ApiFormElementWriteToFieldSettingsEdit
) => {
  const table = getWriteToFieldTable(element, settings);
  return table != null && table.table_type !== ApiTableTypes.custom;
};

export const getFieldWithQuestionnaireSlug = (
  field: ApiField,
  elementId: number,
  isMandatory: boolean = field.is_mandatory,
  rowIndex: number = 0
) => ({
  ...field,
  slug: getWriteToFieldSlug(elementId, field.id, rowIndex, field.type),
  is_mandatory: isMandatory,
});

export const getWriteToFieldFields = (
  element: QuestionElementProps["element"],
  settings: ApiFormElementWriteToFieldSettingsEdit
) => {
  if (
    settings.target_type === ApiFormElementWriteToFieldTargetType.field &&
    settings.field_id != null
  ) {
    const field = element.fields?.find((field) => field.id === settings.field_id);
    return field && isEditableWriteToField(field) ? [field] : [];
  }

  if (
    settings.target_type === ApiFormElementWriteToFieldTargetType.table &&
    settings.table_id != null
  ) {
    const table = getWriteToFieldTable(element, settings);

    // System tables: backend resolves fields into `fields`; `tables` is only table metadata.
    if (table?.table_type !== ApiTableTypes.custom) {
      return element.fields?.filter(isEditableWriteToField) ?? [];
    }

    const tableFields = (
      table?.table_fields ??
      element.fields?.filter((field) => field.table_id === settings.table_id) ??
      []
    ).filter(isEditableWriteToField);
    const selectedTableFields =
      (settings.fill_in_all_columns ?? true)
        ? tableFields
        : tableFields.filter((field) => settings.column_ids_to_fill?.includes(field.id) ?? false);
    const effectiveFromField = element.fields?.find(
      (field) => field.type === ApiFieldTypes.effective_from
    );

    if (!table?.is_historical || !effectiveFromField) return selectedTableFields;

    return [
      effectiveFromField,
      ...selectedTableFields.filter((field) => field.id !== effectiveFromField.id),
    ];
  }

  return [];
};
